// @flow
import React from 'react';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, intlShape } from 'react-intl';

import SelectionItemComponent from '../../common/selection/SelectionItemComponent';
import SelectionComponent from '../../common/selection/SelectionComponent';

import * as abstractWindow from '../../../../abstraction/window';

import {
  getProductSizeIntlPluralKey,
  getProductTypeIntlSingularKey,
  getFullUploadProductSizes,
  getFullUploadCreationLink,
  areFullUploadElementsAvailable,
} from '../../../services/product-type/ProductTypeService';
import type { ProductSizeType } from '../../../../api/types/ProductSizes';
import {
  getProductIdByRoute,
  getProductRouteById,
  getProductSizeRouteById,
} from '../../../services/products-route/ProductsRouteService';
import { getMessageText } from '../../../services/message/MessageService';
import { envUrl } from '../../../services/environment/EnvironmentService';

type Props = {
  config: Object,
  params: { type: string },
  guidelinesCallback: () => void,
  sizeSelectionCallback: (string) => void,
};

type Event = { id: string };

class ProductSizeSelectionComponent extends React.Component {
  constructor(props: Props): void {
    super(props);

    this.state = {
      activeSizeId: null,
    };

    this.productTypeId = getProductIdByRoute(props.params.type);
  }

  hoverOn(event: Event): Function {
    const { id } = event;

    return (): void => {
      this.setState({
        activeSizeId: id,
      });
    };
  }

  hoverOff(event: Event): Function {
    const { id } = event;

    return (): void => {
      if (this.state.activeSizeId === id) {
        this.setState({
          activeSizeId: null,
        });
      }
    };
  }

  onSizeSelected(productType: string, selectedSizeItem: ProductSizeType): Function {
    return (): void => {
      const sizes = getFullUploadProductSizes(productType);
      const sizeData = sizes.find((sizeItem) => sizeItem.id === selectedSizeItem.id);

      if (sizeData) {
        this.props.sizeSelectionCallback(selectedSizeItem.name);

        if (areFullUploadElementsAvailable(selectedSizeItem.id)) {
          const productTypeRoute = getProductRouteById(productType);
          const productSizeRoute = getProductSizeRouteById(selectedSizeItem.id);
          this.props.history.push(envUrl(`/products/upload/${productTypeRoute}/${productSizeRoute}`));
        } else {
          const creationLink = getFullUploadCreationLink(selectedSizeItem.id);
          abstractWindow.location.replace(creationLink);
        }
      }
    };
  }

  getProductTypeName(productTypeId: string): string {
    const productTypeMessage = getProductTypeIntlSingularKey(productTypeId);
    const name = getMessageText(this.context.intl, { message: productTypeMessage, fallbackText: productTypeId });
    return name.toLowerCase();
  }

  getSizeName(sizeId: string): string {
    return this.context.intl.formatMessage(getProductSizeIntlPluralKey(sizeId));
  }

  getCamelCasedSizeName(sizeId: string) {
    const sizeName = this.getSizeName(sizeId);
    return sizeName.replace(/\b\w/g, (word) => word.toUpperCase());
  }

  renderProductSizes(productTypeName: string): Array<Element> {
    const productSizes = getFullUploadProductSizes(this.productTypeId);
    if (productSizes.length === 0) {
      return (
        <div className="product-size-selection-component__message">
          <FormattedMessage
            id="app.fullUpload.sizeSelect.emptyMessage"
            defaultMessage="Sorry, we couldn't find any {productType} sizes"
            values={{ productType: productTypeName }}
          />
        </div>
      );
    }

    return productSizes.map((sizeItem) => {
      return (
        <SelectionItemComponent
          id={sizeItem.id}
          key={`size--${sizeItem.id}`}
          name={this.getCamelCasedSizeName(sizeItem.id)}
          image={sizeItem.image}
          description={sizeItem.physicalSize}
          subDescription={`Artwork Size: ${sizeItem.artworkSize}`}
          linkText={`Make ${this.getCamelCasedSizeName(sizeItem.id)}`}
          onMouseEnter={this.hoverOn(sizeItem)}
          onMouseLeave={this.hoverOff(sizeItem)}
          onSelect={this.onSizeSelected(this.productTypeId, sizeItem)}
          disabled={this.state.activeSizeId !== null && this.state.activeSizeId !== sizeItem.id}
        />
      );
    });
  }

  render(): Element {
    const productTypeName = this.getProductTypeName(this.productTypeId);

    const sizeHeaderDetails = {
      title: {
        id: 'app.fullUpload.sizeSelect.title',
        defaultMessage: 'What size would you like to create?',
        values: { productType: productTypeName },
      },
    };
    const sizeGuidelinesDetails = {
      tipText: {
        id: 'app.fullUpload.sizeSelect.guidelinesTip.text',
        defaultMessage: "Don't forget it's a good idea to review artwork guidelines for your size.",
      },
      tipLink: {
        id: 'app.fullUpload.sizeSelect.guidelinesTip.link',
        defaultMessage: 'View templates and guidelines',
      },
      tipPath: '200570950-MOO-Product-Templates',
    };

    return (
      <SelectionComponent
        config={this.props.config}
        headerDetails={sizeHeaderDetails}
        guidelinesDetails={sizeGuidelinesDetails}
        guidelinesCallback={this.props.guidelinesCallback}
      >
        {this.renderProductSizes(productTypeName)}
      </SelectionComponent>
    );
  }
}

ProductSizeSelectionComponent.displayName = 'ProductSizeSelection';

ProductSizeSelectionComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default withRouter(ProductSizeSelectionComponent);
